import React, { useState } from 'react';

import { Modal, Button, Select, Slider, Card } from 'antd';

import PvfFilterCheckboxes from './pvfFilterCheckboxes';

const fullTableFilterOptions = [
  { name: 'CCT (K)', value: 'cct' },
  {
    name: (
      <span>
        <i>D</i>
        <sub>uv</sub>
      </span>
    ),
    value: 'duv',
  },
  {
    name: (
      <span>
        <i>R</i>
        <sub>f</sub>
      </span>
    ),
    value: 'rf',
  },
  {
    name: (
      <span>
        <i>R</i>
        <sub>g</sub>
      </span>
    ),
    value: 'rg',
  },
  {
    name: (
      <span>
        Local Chroma Shift (R
        <sub>
          cs,h<i>j</i>
        </sub>
        )
      </span>
    ),
    value: 'lcs',
  },
  {
    name: (
      <span>
        Local Hue Shift (R
        <sub>
          hs,h<i>j</i>
        </sub>
        )
      </span>
    ),
    value: 'lhs',
  },
  {
    name: (
      <span>
        Local Color Fidelity (R
        <sub>
          f,h<i>j</i>
        </sub>
        )
      </span>
    ),
    value: 'lcf',
  },
  { name: 'P Level', value: 'preferenceLevel' },
  { name: 'V Level', value: 'vividnessLevel' },
  { name: 'F Level', value: 'fidelityLevel' },
  { name: 'S-cone-opic (mW.m⁻²)', value: 'sConeIrradiance' },
  { name: 'M-cone-opic (mW.m⁻²)', value: 'mConeIrradiance' },
  { name: 'L-cone-opic (mW.m⁻²)', value: 'lConeIrradiance' },
  { name: 'Rhodopic (mW.m⁻²)', value: 'rhodopicIrradiance' },
  { name: 'Melanopic (mW.m⁻²)', value: 'melanopicIrradiance' },
  { name: 'S-cone-opic EDI', value: 'sConeOpicEdl' },
  { name: 'M-cone-opic EDI', value: 'mConeOpicEdl' },
  { name: 'L-cone-opic EDI', value: 'lConeOpicEdl' },
  { name: 'Rhodopic EDI', value: 'rhodopicEdl' },
  { name: 'Melanopic EDI', value: 'melanopicEdl' },
  { name: 'S-cone-opic ELR', value: 'sConeOpicElr' },
  { name: 'M-cone-opic ELR', value: 'mConeOpicElr' },
  { name: 'L-cone-opic ELR', value: 'lConeOpicElr' },
  { name: 'Rhodopic ELR', value: 'rhodopicElr' },
  { name: 'Melanopic ELR', value: 'melanopicElr' },
];

const { Option } = Select;

export const TableFilterModal = ({
  tableFiltering,
  setTableFiltering,
  tableFilterData,
  setTableFilterData,
  memoizedDataSource,
}) => {
  const [tableFilterOptions, setTableFilterOptions] = useState([]);

  const handleSelectFilters = (values) => {
    const tableFilterDataKeys = Object.keys(tableFilterData);
    let updatedTableFilterData = {};

    tableFilterDataKeys.forEach((filterValue) => {
      if (values.includes(filterValue)) {
        Object.assign(updatedTableFilterData, {
          [filterValue]: tableFilterData[filterValue],
        });
      }
    });
    setTableFilterOptions(values);
    setTableFilterData(updatedTableFilterData);
  };

  const handleSliderChange = (values, filterValue, checked) => {
    const newTableFilterData = Object.assign({}, tableFilterData, {
      [filterValue]: values,
    });
    setTableFilterData(newTableFilterData);
  };

  const getSliderMinAndMax = (filterValue, dataSource) => {
    let min = 0;
    let max = 0;

    if (filterValue === 'cct') {
      min = Math.min.apply(
        Math,
        dataSource.map((data) => {
          if (typeof data[filterValue].props.children === 'object') {
            return data[filterValue].props.children.props.children;
          } else {
            return data[filterValue].props.children;
          }
        })
      );
      max = Math.max.apply(
        Math,
        dataSource.map((data) => {
          if (typeof data[filterValue].props.children === 'object') {
            return data[filterValue].props.children.props.children;
          } else {
            return data[filterValue].props.children;
          }
        })
      );
    } else {
      min = Math.min.apply(
        Math,
        dataSource.map((data) => data[filterValue])
      );
      max = Math.max.apply(
        Math,
        dataSource.map((data) => data[filterValue])
      );
    }

    if (['duv', 'lhs'].includes(filterValue)) {
      return { min: min, max: max };
    } else {
      return { min: Math.round(min), max: Math.round(max) };
    }
  };

  const filterOptions = fullTableFilterOptions.map((option, index) => (
    <Option key={index} value={option.value}>
      {option.name}
    </Option>
  ));

  return (
    <Modal
      title='Filter'
      visible={tableFiltering}
      onCancel={() => setTableFiltering(false)}
      footer={[
        <Button
          key='clear'
          onClick={() => {
            setTableFilterData({});
            setTableFilterOptions([]);
          }}
        >
          Clear
        </Button>,
        <Button
          key='ok'
          type='primary'
          onClick={() => setTableFiltering(false)}
        >
          Done
        </Button>,
      ]}
    >
      <Select
        mode='multiple'
        allowClear
        style={{ width: '100%' }}
        placeholder='Select one or more quantities to use as filters'
        onChange={handleSelectFilters}
        value={tableFilterOptions}
      >
        {filterOptions}
      </Select>
      {tableFilterOptions.map((filterValue, index) => {
        const filterSelected = fullTableFilterOptions.find(
          (filterOption) => filterOption.value === filterValue
        );

        const minMax = getSliderMinAndMax(filterValue, memoizedDataSource);
        if (
          ['preferenceLevel', 'vividnessLevel', 'fidelityLevel'].includes(
            filterValue
          )
        ) {
          return (
            <Card
              key={index}
              style={{ width: 470, marginTop: '10px' }}
              hoverable
            >
              <h5>{filterSelected.name}</h5>
              <PvfFilterCheckboxes
                filterValue={filterValue}
                tableFilterData={tableFilterData}
                setTableFilterData={setTableFilterData}
              />
            </Card>
          );
        }
        return (
          <Card key={index} style={{ width: 470, marginTop: '10px' }} hoverable>
            <h5>{filterSelected.name}</h5>
            <Slider
              range
              marks={{ [minMax.min]: minMax.min, [minMax.max]: minMax.max }}
              min={minMax.min}
              max={minMax.max}
              step={
                filterValue === 'duv'
                  ? 0.0001
                  : filterValue === 'lhs'
                  ? 0.01
                  : 1
              }
              onAfterChange={(values) =>
                handleSliderChange(values, filterValue)
              }
            />
          </Card>
        );
      })}
    </Modal>
  );
};
